import { apiClient } from '@/lib/api-client';
import type { ApiSuccess } from '@/lib/api-client';
import type {
  CreateFirstValuationInput,
  FinalizeInput,
  FirstValuationDto,
  SubmitInput,
  ValuationPackage,
} from './types';

/**
 * First Valuation API client.
 *
 * Endpoints (see `backend/src/modules/first_valuations/routes.rs`):
 *   POST /api/monthly-batches/:batchId/first-valuations   { mode, valuation_code? }
 *   GET  /api/monthly-batches/:batchId/first-valuations
 *   GET  /api/first-valuations/:id
 *   GET  /api/first-valuations/:id/packages
 *   PUT  /api/first-valuations/:id/packages               { inbound_package_id, valuation_amount, note? }
 *   POST /api/first-valuations/:id/submit                 { valuation_amount, material_price_reference? }
 *   POST /api/first-valuations/:id/finalize               { final_basis, final_company_id?, final_value?, final_reason? }
 *   POST /api/first-valuations/:id/lock
 *   POST /api/first-valuations/:id/request-revaluation
 *
 * All responses are the blind-isolation-aware `FirstValuationDto`.
 */

export const fetchFirstValuations = async (batchId: string): Promise<FirstValuationDto[]> => {
  const res = await apiClient.get<ApiSuccess<FirstValuationDto[]>>(
    `/monthly-batches/${batchId}/first-valuations`,
  );
  return res.data.data ?? [];
};

export const fetchFirstValuation = async (id: string): Promise<FirstValuationDto> => {
  const res = await apiClient.get<ApiSuccess<FirstValuationDto>>(`/first-valuations/${id}`);
  return res.data.data;
};

export const createFirstValuation = async (
  batchId: string,
  body: CreateFirstValuationInput,
): Promise<FirstValuationDto> => {
  const res = await apiClient.post<ApiSuccess<FirstValuationDto>>(
    `/monthly-batches/${batchId}/first-valuations`,
    body,
  );
  return res.data.data;
};

/**
 * The inbound units this valuation covers, with their photos and the caller's
 * own estimates. The server derives the company from the session, so a
 * company can never read another's numbers through this route.
 */
export const fetchValuationPackages = async (id: string): Promise<ValuationPackage[]> => {
  const res = await apiClient.get<ApiSuccess<ValuationPackage[]>>(
    `/first-valuations/${id}/packages`,
  );
  return res.data.data ?? [];
};

export const upsertValuationPackage = async (
  id: string,
  body: { inbound_package_id: string; valuation_amount: string; note?: string },
): Promise<void> => {
  await apiClient.put(`/first-valuations/${id}/packages`, body);
};

/** File this company's valuation. One company, one valuation. */
export const submitValuation = async (
  id: string,
  body: SubmitInput,
): Promise<FirstValuationDto> => {
  const res = await apiClient.post<ApiSuccess<FirstValuationDto>>(
    `/first-valuations/${id}/submit`,
    body,
  );
  return res.data.data;
};

export const finalizeFirstValuation = async (
  id: string,
  body: FinalizeInput,
): Promise<FirstValuationDto> => {
  const res = await apiClient.post<ApiSuccess<FirstValuationDto>>(
    `/first-valuations/${id}/finalize`,
    body,
  );
  return res.data.data;
};

export const lockFirstValuation = async (id: string): Promise<FirstValuationDto> => {
  const res = await apiClient.post<ApiSuccess<FirstValuationDto>>(`/first-valuations/${id}/lock`);
  return res.data.data;
};

export const requestRevaluation = async (id: string): Promise<FirstValuationDto> => {
  const res = await apiClient.post<ApiSuccess<FirstValuationDto>>(
    `/first-valuations/${id}/request-revaluation`,
  );
  return res.data.data;
};
